// Global Instructions Rule Applied!
//
// Capital Call issuance screen.
//
// Draft-first workflow: header (fund / name / call date / due date) + line
// items + the per-commitment allocation matrix. Every save round-trips
// through POST /capital-calls (cc__save_call) and re-hydrates from the
// returned detail. Compute calculates the per-commitment amounts (fee engine
// for management-fee items, pro-rata allocator otherwise) and keeps the
// calculation with the call (spec A3.12). Issue is atomic on the server
// (spec A2.1) and books the amounts AS SHOWN: create + book the Capital Call
// fund transaction with per-commitment journals -> status Issued. It is
// refused while the call needs Compute (inputs changed since, or never
// computed) - the form says so before the click. Issued calls are
// read-only; Revert (blocked only while the journals sit in a closed FY)
// returns them to Draft by unbooking + trashing the transaction and leaves
// every amount as issued. Recompute is the separate step: with manual
// per-investor edits on the call it asks whether to keep or discard them
// (RecomputeEditsModal).
//
// Amount precedence (spec A2.5): fund-level item effective =
// COALESCE(override, calculated, amount, pct x closed commitments) —
// rendered from the server's prog__item_effective_amount; per-commitment
// effective = COALESCE(override_amount, calculated_amount).

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import DatePicker from '../../../../core/components/DatePicker.tsx'
import { DeleteOutlined, DollarOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import type { EqualizationPreview } from '../../../../state_manager/data_models/capital_call_equalization.ts'
import { exportJsonToExcel } from '../../../../core/services/utilitiesService.ts'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { removeTab, transformFormTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { TABLE_CAPITAL_CALLS, TABLE_FUND_TRANSACTIONS } from '../../../../state_manager/globals.ts'
import {
  useNumberFormat,
  useNumberDisplay,
  decimalToPercent,
  percentToDecimal
} from '../../../../core/utils/inputNumberFormat.ts'
import {
  CapitalCallDetail,
  CapitalCallSavePayload
} from '../../../../state_manager/data_models/capital_call_detail.ts'
import {
  fetchCapitalCallDetail,
  fetchFundEqualizationPreview,
  saveCapitalCall,
  renameCapitalCall,
  computeCapitalCall,
  clearCapitalCallOverrides,
  issueCapitalCall,
  revertCapitalCall,
  trashCapitalCall
} from '../../services/capitalCallsService.ts'
import {
  updateCapitalCallRecord,
  removeCapitalCall
} from '../../../../state_manager/state_update_actions/capital_calls/index.ts'
// consolidated-call context (owner ruling 2026-08-08: the plan modal is
// gone — this form is the single surface; fgc__save_call stays the
// canonical header writer for group-linked calls)
import {
  fetchFundGroupCallDetail,
  fetchFundGroupDashboard,
  saveFundGroupCall,
  issueFundGroupCall,
  revertFundGroupCall,
  deleteFundGroupCall,
  bookFundGroupTransfer,
  revertFundGroupTransfer,
  deleteFundGroupTransfer
} from '../../../fund_groups/services/fundGroupsService.ts'
import type { FundGroupCallDetail } from '../../../../state_manager/data_models/fund_group.ts'
import ConsolidatedCallBanner, { ConsolidatedSizingValue } from './ConsolidatedCallBanner.tsx'
import TransferCallBanner from './TransferCallBanner.tsx'
import PaymentEntryGrid from '../PaymentEntryGrid.tsx'
import NoticePeriodAlert from '../NoticePeriodAlert.tsx'
import NoticesSection from '../NoticesSection.tsx'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'
import CallAllocationTable, {
  AllocationCell,
  AllocationItemType,
  AllocationRow,
  ConsolidatedBasis,
  allocationExport,
  allocationLayout,
  sortAllocationRows
} from './CallAllocationTable.tsx'
import RecomputeEditsModal, { ManualEdit } from './RecomputeEditsModal.tsx'
import {
  CallLineBucket,
  CommitmentTerms,
  bucketCountsTowardCommitment,
  callLineBucket,
  outsideCommitmentLabel
} from '../../utils/commitmentTerms.ts'

const { Title, Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const LIST_PATH = '/capital-calls'
// Status labels the cc__ orchestrators gate on (cfg.capital_call_statuses).
const STATUS_LABEL_DRAFT = 'Draft'
const STATUS_LABEL_ISSUED = 'Issued'

interface FullCapitalCallFormProps {
  recordId?: string | null
  isNew?: boolean
}

interface ItemRow {
  key: string
  id?: string
  item_type_id: string | null
  description: string
  /** notebook "Type" toggle: a call item is EITHER a fixed amount OR a % of committed */
  input_type: 'amount' | 'percent'
  amount: number | null
  percentage: number | null
  override_amount: number | null
  mgmt_fee_period_from: string | null
  mgmt_fee_period_to: string | null
  /** structured calls: the one line that absorbs call total - siblings */
  is_balance: boolean
  /** late-closer catch-up items: engine-derived from the issued ledger */
  catchup_kind_id: number | null
  catchup_kind_label: string | null
  /** which source lines the catch-up settles (item type x rate|fixed) */
  catchup_source_item_type_id: string | null
  catchup_source_spec: string | null
  calculated_amount: number | null
  effective_amount: number | null
  per_commitment_total: number | null
  /** fee lines: the line at the fund's headline rate (no side letters) — the balance line is net of THIS, not of the effective fee */
  headline_total: number | null
  /** the line rate as computed (spec A3.12): a fee line shows it in the allocation table's header */
  called_rate: number | null
}

let rowKeySeq = 0
const nextRowKey = () => `row-${++rowKeySeq}`

const fmtMoney = (value: number | null | undefined, symbol?: string | null) =>
  value == null ? '-' : `${symbol ?? ''}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`

export default function FullCapitalCallForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullCapitalCallFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()
  const [modalApi, modalContextHolder] = Modal.useModal()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId
  // New-record tabs are keyed per prefill query (tabSlice.addFormTab: the
  // ?fund=<id> / ?fund_group=<id> openers), so the key must carry the
  // mount-time search string or the title update and the post-save tab
  // swap miss the real tab. Snapshot it: the global location later reflects
  // whichever tab is active, not this one.
  const [mountSearch] = useState(() => (isNew ? location.search : ''))
  const formTabKey = `${TABLE_CAPITAL_CALLS}-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}${mountSearch}`

  const fundGroups = useSelector((state: any) => state.globalConfigs?.fund_groups ?? [])
  const accItems = useSelector((state: any) => state.globalConfigs?.acc_items ?? [])
  const catchupKinds = useSelector((state: any) => state.globalConfigs?.catchup_kinds ?? [])
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')

  const [detail, setDetail] = useState<CapitalCallDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [paymentsOpen, setPaymentsOpen] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Header fields
  const [fundId, setFundId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [callDate, setCallDate] = useState<string | null>(null)
  const [dueDate, setDueDate] = useState<string | null>(null)

  // Line items split per the notebook layout: Call Items (fixed amount / % of
  // committed) and Management Fees (fee-period rows the engine computes).
  // Plus the per-commitment override edits of this session (pc row id ->
  // override as typed; null = cleared). Only edited cells are in the map:
  // every other cell reads its saved override.
  const [items, setItems] = useState<ItemRow[]>([])
  const [feeItems, setFeeItems] = useState<ItemRow[]>([])
  const [pcOverrides, setPcOverrides] = useState<Record<string, number | null>>({})
  const [recomputeOpen, setRecomputeOpen] = useState(false)

  // Structured-call total: '% of committed' / set amount / none (bottom-up)
  const [totalMode, setTotalMode] = useState<'none' | 'percent' | 'amount'>('none')
  const [totalPercentage, setTotalPercentage] = useState<number | null>(null)
  const [totalAmount, setTotalAmount] = useState<number | null>(null)
  // "Include management fees" / "Include expenses" (capital_calls.
  // fees_in_total / expenses_in_total): whether the fee lines / the
  // expenses line are part of the call amount or added on top of it; null =
  // never chosen, the fund-terms rule applies (see *InTotalEffective)
  const [feesInTotal, setFeesInTotal] = useState<boolean | null>(null)
  const [expensesInTotal, setExpensesInTotal] = useState<boolean | null>(null)

  // Consolidated-call context: ?fund_group= puts a NEW form into
  // new-consolidated mode; an existing call's fund_group_call_id loads the
  // fgc detail for the banner. Sizing lives in the banner and resolves
  // against the Closed SUBSCRIPTION pool (never capital_calls.total_percentage).
  const [newGroupId, setNewGroupId] = useState<string | null>(null)
  const [groupCallDetail, setGroupCallDetail] = useState<FundGroupCallDetail | null>(null)
  const [groupPool, setGroupPool] = useState<{ pool: number | null; poolFmt: string | null }>({
    pool: null,
    poolFmt: null
  })
  const [groupSizing, setGroupSizing] = useState<ConsolidatedSizingValue>({
    sizingMode: 'amount',
    totalAmount: null,
    totalPercentage: null,
    itemTypeId: null,
    description: ''
  })

  const statusLabel = detail?.call?.status_label ?? STATUS_LABEL_DRAFT
  const isIssued = statusLabel === STATUS_LABEL_ISSUED
  const readOnly = isIssued
  // an Issued call stays renameable (owner 2026-09-27): cc__rename_call
  // also relabels the booked descriptions that carried the old name
  const renamePending =
    isIssued && name.trim() !== '' && name.trim() !== (detail?.call?.name ?? '')
  // group-linked calls (gross consolidated call / transfer-derived satellite
  // call): items stay editable while Draft. The LIFECYCLE (issue / revert /
  // delete) runs through the fgc__ orchestrators: handleGroupIssue & co for
  // the gross call, handleTransferBook & co for a transfer-derived call.
  const isGroupLinked = Boolean(
    detail?.call?.fund_group_call_id || detail?.call?.fund_group_transfer_id
  )
  // the gross consolidated call (has a fund_group_calls header + banner);
  // transfer-derived satellite calls only get the informational alert
  const groupCallId = (detail?.call?.fund_group_call_id as string | null | undefined) ?? null
  const isGroupCallLinked = Boolean(groupCallId)
  // a satellite call derived by an intercompany transfer (owner 2026-09-28):
  // the transfer is BUILT here like any call - sizing, lines, fee periods,
  // balance, Compute (the SBIC fee solver) - and Book transfer / Revert /
  // Delete run the fgc__ transfer orchestrators
  const transferContext = detail?.transfer ?? null
  const transferId = transferContext?.fund_group_transfers__id ?? null
  const isTransferCall = Boolean(detail?.call?.fund_group_transfer_id)
  // an equalization (late-closer catch-up) call of a consolidated fund
  // (owner 2026-09-27): part of the group - listed on its Calls tab, its
  // lifecycle runs through the fgc__ orchestrators, it funds transfers - but
  // unsized: its catch-up lines size it, so the gross-sizing banner and the
  // banner-owned Call Amount do not apply
  const isGroupEqualization = Boolean(groupCallDetail?.call?.fund_group_calls__is_equalization)
  const isGroupSized = isGroupCallLinked && !isGroupEqualization
  const isNewConsolidated = !detail?.call?.id && Boolean(newGroupId)
  const currencySymbol = detail?.call?.currency_symbol ?? null
  const consolidatedGroupId =
    (groupCallDetail?.call?.fund_groups__id as string | null | undefined) ?? newGroupId ?? null
  const consolidatedGroupName = useMemo(
    () => fundGroups.find((g: { id: string }) => g.id === consolidatedGroupId)?.name ?? null,
    [fundGroups, consolidatedGroupId]
  )

  // Item types are cfg-driven: only accounting items flagged call_component
  // (the Capital contribution* equity components) can appear on a call.
  const itemTypeOptions = useMemo(
    () => accItems.filter((i: any) => i.call_component).map((i: any) => ({ value: i.id, label: i.label })),
    [accItems]
  )
  // Preset item types the structured-call toggles and balance line target
  const expensesTypeId = useMemo(
    () =>
      itemTypeOptions.find((o: any) => o.label === 'Capital contribution - expenses')?.value ??
      itemTypeOptions.find((o: any) => /expenses/i.test(o.label) && !/investments/i.test(o.label))?.value ??
      null,
    [itemTypeOptions]
  )
  const balanceTypeId = useMemo(
    () =>
      itemTypeOptions.find((o: any) => o.label === 'Capital contribution')?.value ??
      itemTypeOptions[0]?.value ??
      null,
    [itemTypeOptions]
  )

  const hydrateFromDetail = useCallback((d: CapitalCallDetail) => {
    setDetail(d)
    // Keep the Capital Calls grid row in step: the list tab stays mounted
    // and only refetches on a filter/sort change, so every path that
    // re-hydrates this form (save, compute, issue, revert) also patches
    // the row's Amount + statuses. Amount mirrors view__capital_calls:
    // the per-commitment sum once the call is computed (what Issue books,
    // incl. investor-level manual edits), else the item-level effectives.
    if (d.call.id) {
      const total =
        d.per_commitment.length > 0
          ? d.totals.called_total
          : d.items.reduce((acc, i) => acc + (i.prog__item_effective_amount ?? 0), 0)
      dispatch(
        updateCapitalCallRecord({
          capitalCallId: d.call.id,
          updates: {
            capital_calls__name: d.call.name ?? null,
            capital_calls__call_date: d.call.call_date ?? null,
            capital_calls__due_date: d.call.due_date ?? null,
            capital_calls__total_amount: total,
            capital_calls__total_amount_fmt: fmtMoney(Math.round(total), d.call.currency_symbol),
            cfg_capital_call_statuses__label: d.call.status_label ?? null,
            fund_transactions__id: d.transaction?.id ?? null,
            cfg_transaction_statuses__label: d.transaction?.status_label ?? null
          }
        })
      )
    }
    setFundId(d.call.fund_id)
    setName(d.call.name ?? '')
    setCallDate(d.call.call_date ?? null)
    setDueDate(d.call.due_date ?? null)
    const callAny = d.call as any
    if (callAny.total_percentage != null) {
      setTotalMode('percent')
      setTotalPercentage(callAny.total_percentage)
      setTotalAmount(null)
    } else if (callAny.total_amount != null) {
      setTotalMode('amount')
      setTotalAmount(callAny.total_amount)
      setTotalPercentage(null)
    } else {
      setTotalMode('none')
      setTotalPercentage(null)
      setTotalAmount(null)
    }
    setFeesInTotal(d.call.fees_in_total ?? null)
    setExpensesInTotal(d.call.expenses_in_total ?? null)
    const rows = d.items.map((i) => ({
      key: i.id,
      id: i.id,
      item_type_id: i.item_type_id,
      description: i.description ?? '',
      input_type: (i.percentage != null && i.amount == null ? 'percent' : 'amount') as 'amount' | 'percent',
      amount: i.amount ?? null,
      percentage: i.percentage ?? null,
      override_amount: i.override_amount ?? null,
      mgmt_fee_period_from: i.mgmt_fee_period_from ?? null,
      mgmt_fee_period_to: i.mgmt_fee_period_to ?? null,
      is_balance: Boolean((i as any).is_balance),
      catchup_kind_id: (i as any).catchup_kind_id ?? null,
      catchup_kind_label: (i as any).catchup_kind_label ?? null,
      catchup_source_item_type_id: (i as any).catchup_source_item_type_id ?? null,
      catchup_source_spec: (i as any).catchup_source_spec ?? null,
      calculated_amount: i.calculated_amount ?? null,
      effective_amount: i.prog__item_effective_amount ?? null,
      per_commitment_total: i.prog__per_commitment_total ?? null,
      headline_total: i.prog__item_headline_total ?? null,
      called_rate: i.called_rate ?? null
    }))
    setItems(rows.filter((r) => !r.mgmt_fee_period_from && !r.mgmt_fee_period_to))
    setFeeItems(rows.filter((r) => r.mgmt_fee_period_from || r.mgmt_fee_period_to))
    // the Override inputs show the saved overrides again: no unsaved edits
    setPcOverrides({})
    setDirty(false)
  }, [dispatch])

  // Cold-load detail (screen-local; the hard-refresh path is the normal path).
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      dispatch(updateTabTitle({ key: formTabKey, title: 'New Capital Call' }))
      setItems([emptyItemRow()])
      setFeeItems([])
      // ?fund=<id> prefill — the fund Capital Calls tab's New Capital Call
      // button opens the form pre-scoped to its fund
      const search = new URLSearchParams(mountSearch)
      const fundParam = search.get('fund')
      if (fundParam) setFundId(fundParam)
      // ?fund_group=<id> — "New Consolidated Call" (group Capital Calls
      // tab): first save goes through fgc__save_call, which creates the
      // plan header AND materialises this Draft call on the primary fund
      const groupParam = search.get('fund_group')
      if (groupParam) {
        setNewGroupId(groupParam)
        dispatch(updateTabTitle({ key: formTabKey, title: 'New Consolidated Call' }))
      }
      return
    }
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchCapitalCallDetail(String(recordId), token)
      if (cancelled) return
      if (res.error || !res.data) {
        if (res.error) showErrorToast(res.error)
        setNotFound(true)
        setLoading(false)
        return
      }
      hydrateFromDetail(res.data)
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `${res.data.call.name || 'Capital Call'} — ${res.data.call.fund_name ?? ''}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNew, dispatch, formTabKey, hydrateFromDetail, mountSearch])

  const markDirty = () => setDirty(true)

  function isNewRecord() {
    return !detail?.call?.id
  }

  // hydrate the consolidated banner: fgc__call_detail for linked calls
  // (sizing, pool, resolved gross, group id), the group dashboard's Closed
  // pool for new-consolidated mode
  const refreshGroupCallDetail = useCallback(async () => {
    if (!groupCallId) return
    const token = getAccessToken(store.getState().auth)
    const res = await fetchFundGroupCallDetail(groupCallId, token)
    if (res.error || !res.data) return
    setGroupCallDetail(res.data)
    const c = res.data.call
    setGroupSizing({
      sizingMode: c.fund_group_calls__total_percentage != null ? 'percentage' : 'amount',
      totalAmount: c.fund_group_calls__total_amount ?? null,
      // stored as a decimal fraction; edited as a percentage
      totalPercentage:
        c.fund_group_calls__total_percentage != null
          ? c.fund_group_calls__total_percentage * 100
          : null,
      itemTypeId: c.fund_group_calls__item_type_id ?? null,
      description: c.fund_group_calls__description ?? ''
    })
  }, [groupCallId])

  useEffect(() => {
    if (groupCallId) {
      void refreshGroupCallDetail()
      return
    }
    if (!newGroupId) return
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroupDashboard(newGroupId, token)
      if (cancelled || !res.data) return
      const d = res.data as { prog__closed_capital?: number | null; prog__closed_capital_fmt?: string | null }
      setGroupPool({ pool: d.prog__closed_capital ?? null, poolFmt: d.prog__closed_capital_fmt ?? null })
    })()
    return () => {
      cancelled = true
    }
  }, [groupCallId, newGroupId, refreshGroupCallDetail])

  // ----- item grid handlers -----
  function emptyItemRow(): ItemRow {
    return {
      key: nextRowKey(),
      item_type_id: null,
      description: '',
      input_type: 'amount',
      amount: null,
      percentage: null,
      override_amount: null,
      mgmt_fee_period_from: null,
      mgmt_fee_period_to: null,
      is_balance: false,
      catchup_kind_id: null,
      catchup_kind_label: null,
      catchup_source_item_type_id: null,
      catchup_source_spec: null,
      calculated_amount: null,
      effective_amount: null,
      per_commitment_total: null,
      headline_total: null,
      called_rate: null
    }
  }

  const updateItem = (key: string, patch: Partial<ItemRow>) => {
    setItems((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    markDirty()
  }
  const addItem = () => {
    setItems((prev) => [...prev, emptyItemRow()])
    markDirty()
  }
  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((r) => r.key !== key))
    markDirty()
  }

  const updateFeeItem = (key: string, patch: Partial<ItemRow>) => {
    setFeeItems((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    markDirty()
  }
  const addFeeItem = () => {
    // default the accounting item to the fees component when one exists
    const feesOption = itemTypeOptions.find((o: any) => /fees/i.test(o.label))
    setFeeItems((prev) => [
      ...prev,
      { ...emptyItemRow(), item_type_id: feesOption?.value ?? null }
    ])
    markDirty()
  }
  const removeFeeItem = (key: string) => {
    setFeeItems((prev) => prev.filter((r) => r.key !== key))
    markDirty()
  }

  const setPcOverride = (pcId: string, value: number | null) => {
    setPcOverrides((prev) => ({ ...prev, [pcId]: value }))
    markDirty()
  }

  // ----- structured-call total + preset line toggles -----
  const handleTotalModeChange = (mode: 'none' | 'percent' | 'amount') => {
    setTotalMode(mode)
    if (mode === 'none') {
      // a balance line cannot exist without a call total
      setItems((prev) => prev.filter((r) => !r.is_balance))
    } else if (balanceTypeId) {
      setItems((prev) =>
        prev.some((r) => r.is_balance)
          ? prev
          : [...prev, { ...emptyItemRow(), item_type_id: balanceTypeId, is_balance: true }]
      )
    }
    markDirty()
  }

  // "Include management fees" / "Include expenses" decide where the fee
  // lines / the expenses line sit, not whether there are any (owner
  // 2026-10-15): inside the call amount (the balance line nets them) or
  // added on top. The lines themselves are ordinary: a fee period under
  // Management Fees, the expenses on their call item.
  const handleFeesInTotalChange = (on: boolean) => {
    setFeesInTotal(on)
    markDirty()
  }
  const handleExpensesInTotalChange = (on: boolean) => {
    setExpensesInTotal(on)
    markDirty()
  }

  // ----- save / compute / issue / revert / delete -----

  // The new-call form seeds one blank call-item row; a row the user never
  // touched must not block a fees-only save (or be sent to the RPC at all).
  const isBlankItemRow = (r: ItemRow) =>
    !r.id &&
    !r.item_type_id &&
    !r.description &&
    r.amount == null &&
    r.percentage == null &&
    r.override_amount == null

  const buildSavePayload = useCallback((): CapitalCallSavePayload => {
    const allRows = [...items.filter((r) => !isBlankItemRow(r)), ...feeItems]
    // per_commitment_overrides is a targeted-update list (the RPC keeps
    // unlisted rows as-is), so send only rows whose override actually
    // changed — and never rows whose parent item was removed this session:
    // the save trashes those rows before applying overrides, and a stale
    // targeted update against them fails the whole save.
    const keptItemTypes = new Set(allRows.map((r) => r.item_type_id))
    const serverPc = new Map((detail?.per_commitment ?? []).map((pc) => [pc.id, pc]))
    const perCommitmentOverrides = Object.entries(pcOverrides)
      .filter(([id, value]) => {
        const pc = serverPc.get(id)
        if (!pc || !keptItemTypes.has(pc.item_type_id)) return false
        // the edited value is the manual OVERRIDE; a row needs a targeted
        // update only when it differs from the saved one. null means the
        // user cleared the input: the row falls back to its calculated
        // amount.
        return (value ?? null) !== (pc.override_amount ?? null)
      })
      .map(([id, value]) => ({ id, override_amount: value ?? null }))
    return {
      ...(detail?.call?.id ? { id: detail.call.id } : { fund_id: fundId as string }),
      name: name || null,
      call_date: callDate,
      due_date: dueDate,
      total_percentage: totalMode === 'percent' ? totalPercentage : null,
      total_amount: totalMode === 'amount' ? totalAmount : null,
      fees_in_total: feesInTotal,
      expenses_in_total: expensesInTotal,
      items: allRows.map((i, idx) => ({
        ...(i.id ? { id: i.id } : {}),
        item_type_id: i.item_type_id as string,
        description: i.description || null,
        // the Type toggle makes amount and percentage mutually exclusive;
        // balance rows carry neither (their amount is derived server-side)
        amount: i.is_balance ? null : i.input_type === 'amount' ? i.amount : null,
        percentage: i.is_balance ? null : i.input_type === 'percent' ? i.percentage : null,
        override_amount: i.override_amount,
        mgmt_fee_period_from: i.mgmt_fee_period_from,
        mgmt_fee_period_to: i.mgmt_fee_period_to,
        is_balance: i.is_balance,
        catchup_kind_id: i.catchup_kind_id,
        catchup_source_item_type_id: i.catchup_source_item_type_id,
        catchup_source_spec: i.catchup_source_spec,
        display_order: idx + 1
      })),
      per_commitment_overrides: perCommitmentOverrides
    }
  }, [detail, fundId, name, callDate, dueDate, totalMode, totalPercentage, totalAmount, feesInTotal, expensesInTotal, items, feeItems, pcOverrides])

  // Returns true when the save round-trip fully succeeded (Recompute chains
  // off it: unsaved edits are persisted first so a recompute cannot discard
  // them).
  const handleSave = useCallback(async (): Promise<boolean> => {
    // NEW-CONSOLIDATED: the first save creates the fund_group_calls plan
    // header via fgc__save_call, which materialises the Draft primary-fund
    // call — then the tab swaps onto the created record (standalone wasNew
    // pattern). Items are added after the swap.
    if (isNewConsolidated) {
      if (!name) {
        showErrorToast('Name is required')
        return false
      }
      if (!groupSizing.itemTypeId) {
        showErrorToast('Select the drawdown (balance) line accounting item')
        return false
      }
      if (groupSizing.sizingMode === 'amount' && groupSizing.totalAmount == null) {
        showErrorToast('Enter the gross call amount')
        return false
      }
      if (groupSizing.sizingMode === 'percentage' && groupSizing.totalPercentage == null) {
        showErrorToast('Enter the percentage of consolidated commitments')
        return false
      }
      setSaving(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await saveFundGroupCall(
          {
            fund_group_id: newGroupId as string,
            name: name || null,
            call_date: callDate,
            due_date: dueDate,
            item_type_id: groupSizing.itemTypeId,
            description: groupSizing.description || null,
            total_amount: groupSizing.sizingMode === 'amount' ? groupSizing.totalAmount : null,
            total_percentage:
              groupSizing.sizingMode === 'percentage' ? (groupSizing.totalPercentage ?? 0) / 100 : null
          },
          token
        )
        const createdCallId = res.data?.primary_call?.capital_calls__id
        if (!res.success || !createdCallId) {
          showErrorToast(res.error || 'Failed to create the consolidated call')
          return false
        }
        toast.success(
          'Consolidated call created — add fee or expense lines here, Recompute, then Issue when ready'
        )
        // morph the "new" tab into the edit tab in place (no remove+re-add)
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_CAPITAL_CALLS,
            recordId: createdCallId,
            title: name || 'Capital Call'
          })
        )
        navigate(`/${TABLE_CAPITAL_CALLS}/forms/full/${createdCallId}`, { replace: true })
        return true
      } finally {
        setSaving(false)
      }
    }

    if (!fundId) {
      showErrorToast('Fund is required')
      return false
    }
    const liveItems = items.filter((r) => !isBlankItemRow(r))
    if ([...liveItems, ...feeItems].some((i) => !i.item_type_id)) {
      showErrorToast('Every line item needs an item type')
      return false
    }
    if (totalMode === 'percent' && totalPercentage == null) {
      showErrorToast('Enter the call total percentage')
      return false
    }
    if (totalMode === 'amount' && totalAmount == null) {
      showErrorToast('Enter the call total amount')
      return false
    }
    const missingAmount = liveItems.find(
      (i) =>
        !i.is_balance &&
        // catch-up rows are engine-derived - they never carry an input
        i.catchup_kind_id == null &&
        !i.catchup_kind_label &&
        (i.input_type === 'amount' ? i.amount == null : i.percentage == null) &&
        i.override_amount == null
    )
    if (missingAmount) {
      showErrorToast('Every call item needs an amount or percentage (or an override)')
      return false
    }
    const missingPeriod = feeItems.find((i) => !i.mgmt_fee_period_from || !i.mgmt_fee_period_to)
    if (missingPeriod) {
      showErrorToast('Every management-fee row needs both period dates')
      return false
    }

    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)

      // GROUP-LINKED DRAFT (dual write): fgc__save_call is the single
      // canonical writer for the header + gross sizing — it re-materialises
      // the call header. The cc__save_call payload then carries ITEMS ONLY
      // (header keys omitted; presence-checked updates leave the just-synced
      // header alone). This also fixes the historical clobber where
      // Preview split / Issue silently reverted form-made header edits to
      // the stale plan values.
      let payload = buildSavePayload()
      if (groupCallId && !readOnly) {
        const gres = await saveFundGroupCall(
          {
            id: groupCallId,
            name: name || null,
            call_date: callDate,
            due_date: dueDate,
            item_type_id: groupSizing.itemTypeId,
            description: groupSizing.description || null,
            total_amount: groupSizing.sizingMode === 'amount' ? groupSizing.totalAmount : null,
            total_percentage:
              groupSizing.sizingMode === 'percentage' ? (groupSizing.totalPercentage ?? 0) / 100 : null
          },
          token
        )
        if (!gres.success) {
          showErrorToast(gres.error || 'Failed to save the consolidated call header')
          return false
        }
        if (isGroupEqualization) {
          // an equalization call keeps its own (native) Call Amount spec
          const { name: _n, call_date: _cd, due_date: _dd, ...itemsOnly } = payload
          payload = itemsOnly
        } else {
          const { name: _n, call_date: _cd, due_date: _dd, total_amount: _ta, total_percentage: _tp, ...itemsOnly } = payload
          payload = itemsOnly
        }
      }

      const res = await saveCapitalCall(payload, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to save capital call')
        return false
      }

      const wasNew = !detail?.call?.id
      hydrateFromDetail(res.data)
      if (groupCallId) void refreshGroupCallDetail()
      toast.success(wasNew ? 'Capital call created' : 'Capital call saved')

      if (wasNew) {
        // Morph the "new" tab into the edit tab in place (no remove+re-add:
        // that left the opener tab visible for one paint — screen flash).
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_CAPITAL_CALLS,
            recordId: res.data.call.id,
            title: `${res.data.call.name || 'Capital Call'} — ${res.data.call.fund_name ?? ''}`
          })
        )
        navigate(`/${TABLE_CAPITAL_CALLS}/forms/full/${res.data.call.id}`, { replace: true })
      }
      return true
    } finally {
      setSaving(false)
    }
  }, [fundId, items, feeItems, totalMode, totalPercentage, totalAmount, detail, buildSavePayload, dispatch, formTabKey, hydrateFromDetail, navigate, isNewConsolidated, newGroupId, groupCallId, groupSizing, readOnly, name, callDate, dueDate, refreshGroupCallDetail, isGroupEqualization])

  // Rename an Issued call in place - no revert to Draft needed
  const handleRename = useCallback(async () => {
    if (!detail?.call?.id) return
    setSaving(true)
    try {
      const res = await renameCapitalCall(detail.call.id, name, getAccessToken(store.getState().auth))
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to rename the capital call')
        return
      }
      hydrateFromDetail(res.data)
      if (groupCallId) void refreshGroupCallDetail()
      toast.success('Capital call renamed')
    } finally {
      setSaving(false)
    }
  }, [detail, name, hydrateFromDetail, groupCallId, refreshGroupCallDetail])

  // A cell's manual override as it stands in the form: this session's edit
  // if there is one, else the saved override (override_amount is written by
  // a person only - spec A3.12, open point 6).
  const pcOverride = useCallback(
    (pc: AllocationCell | undefined): number | null => {
      if (!pc) return null
      return pc.id in pcOverrides ? pcOverrides[pc.id] : pc.override_amount ?? null
    },
    [pcOverrides]
  )

  // Every manual per-investor edit on the call, saved or not: what the
  // Recompute dialog lists. Rows of a line removed in this session are left
  // out - the save that precedes a recompute trashes them.
  const manualEdits = useMemo((): ManualEdit[] => {
    const liveTypes = new Set([...items, ...feeItems].map((r) => r.item_type_id))
    return (detail?.per_commitment ?? [])
      .filter((pc) => liveTypes.has(pc.item_type_id) && pcOverride(pc) != null)
      .map((pc) => ({
        key: pc.id,
        investor: pc.investors__name ?? null,
        line:
          itemTypeOptions.find((o: { value: string; label: string }) => o.value === pc.item_type_id)?.label ??
          pc.item_type_label ??
          'Item',
        override: pcOverride(pc) as number,
        calculated: pc.calculated_amount ?? null
      }))
      .sort(
        (a, b) => (a.investor ?? '').localeCompare(b.investor ?? '') || a.line.localeCompare(b.line)
      )
  }, [detail, items, feeItems, itemTypeOptions, pcOverride])

  // Compute / Recompute (spec A3.12, owner 2026-10-03): recalculating is its
  // own step. Unsaved changes are saved first, so a recompute never discards
  // what was typed. 'keep' is a plain compute - the engines leave a manual
  // override alone; 'discard' clears every override first (cc__clear_overrides).
  const runCompute = useCallback(
    async (edits: 'none' | 'keep' | 'discard') => {
      if (!detail?.call?.id) return
      if (dirty) {
        const saved = await handleSave()
        if (!saved) return
      }
      setActionLoading(true)
      try {
        const token = getAccessToken(store.getState().auth)
        if (edits === 'discard') {
          const cleared = await clearCapitalCallOverrides(detail.call.id, token)
          if (cleared.error || !cleared.data) {
            showErrorToast(cleared.error || 'Failed to clear the manual edits')
            return
          }
        }
        const res = await computeCapitalCall(detail.call.id, token)
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to compute amounts')
          return
        }
        hydrateFromDetail(res.data)
        const rounds = res.data.fee_solver_rounds ?? null
        toast.success(
          (edits === 'discard'
            ? 'Manual edits discarded: every amount recomputed'
            : edits === 'keep'
              ? 'Amounts recomputed: manual edits kept'
              : 'Per-commitment amounts computed') +
            (rounds && rounds > 1 ? ` — the satellite fees settled after ${rounds} rounds` : '')
        )
      } finally {
        setActionLoading(false)
      }
    },
    [detail, dirty, handleSave, hydrateFromDetail]
  )

  // No manual edits: run straight away. With manual edits the dialog lists
  // them and asks: keep (the default) or discard.
  const handleCompute = useCallback(() => {
    if (!detail?.call?.id) return
    if (manualEdits.length === 0) {
      void runCompute('none')
      return
    }
    setRecomputeOpen(true)
  }, [detail, manualEdits, runCompute])

  // Re-read the call (and its consolidated header): after an fgc__ action,
  // and after a refused Issue so the form shows why (the call may have been
  // changed elsewhere since it was opened).
  const reloadDetail = useCallback(async () => {
    if (!detail?.call?.id) return
    const token = getAccessToken(store.getState().auth)
    const res = await fetchCapitalCallDetail(detail.call.id, token)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'Failed to reload the capital call')
      return
    }
    hydrateFromDetail(res.data)
    void refreshGroupCallDetail()
  }, [detail, hydrateFromDetail, refreshGroupCallDetail])

  const handleIssue = useCallback(() => {
    if (!detail?.call?.id) return
    const commitments = new Set(detail.per_commitment.map((pc) => pc.fund_commitment_id)).size
    modalApi.confirm({
      title: 'Issue this capital call?',
      content: (
        <div>
          <p>
            The amounts shown are booked as they stand: a booked Capital Call transaction is created
            with per-commitment journals, and the call becomes read-only.
          </p>
          <p className='mb-0'>
            <Text strong>{detail.call.fund_name}</Text>
            {' — '}
            {fmtMoney(detail.totals.called_total, currencySymbol)}
            {commitments ? ` across ${commitments} commitments` : ''}
          </p>
        </div>
      ),
      okText: 'Issue',
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = await issueCapitalCall(detail.call.id, token)
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to issue capital call')
          void reloadDetail()
          return
        }
        hydrateFromDetail(res.data)
        toast.success('Capital call issued and booked')
        dispatch(
          updateCapitalCallRecord({
            capitalCallId: detail.call.id,
            updates: {
              cfg_capital_call_statuses__label: STATUS_LABEL_ISSUED,
              cfg_transaction_statuses__label: 'Booked'
            }
          })
        )
      }
    })
  }, [detail, currencySymbol, dispatch, hydrateFromDetail, modalApi, reloadDetail])

  const handleRevert = useCallback(() => {
    if (!detail?.call?.id) return
    modalApi.confirm({
      title: 'Revert this capital call to Draft?',
      content:
        (detail.totals.paid_total ?? 0) > 0
          ? `The booked Capital Call transaction and its journals are removed. ${fmtMoney(detail.totals.paid_total, currencySymbol)} of LP payments currently applied to this call stay booked and read as prepayments until it is reissued. Blocked only when the journals sit in a closed financial year.`
          : 'The booked Capital Call transaction and its journals are removed. Blocked only when the journals sit in a closed financial year.',
      okText: 'Revert',
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = await revertCapitalCall(detail.call.id, token)
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to revert capital call')
          return
        }
        hydrateFromDetail(res.data)
        toast.success('Capital call reverted to Draft')
        dispatch(
          updateCapitalCallRecord({
            capitalCallId: detail.call.id,
            updates: {
              cfg_capital_call_statuses__label: STATUS_LABEL_DRAFT,
              cfg_transaction_statuses__label: null
            }
          })
        )
      }
    })
  }, [detail, dispatch, hydrateFromDetail, modalApi, currencySymbol])

  const handleDelete = useCallback(() => {
    if (!detail?.call?.id) return
    modalApi.confirm({
      title: 'Delete this capital call?',
      content: 'The call, its line items and per-commitment allocations will be moved to trash.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = await trashCapitalCall(detail.call.id, token)
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        toast.success('Capital call deleted')
        dispatch(removeCapitalCall({ capitalCallId: detail.call.id }))
        navigate(LIST_PATH)
        dispatch(removeTab({ key: formTabKey }))
      }
    })
  }, [detail, dispatch, navigate, formTabKey, modalApi])

  // Consolidated (gross) call lifecycle (owner 2026-09-16): the server's
  // cc__ orchestrators reject issue / revert / delete on a group-linked
  // call, so the same buttons run the fgc__ orchestrators instead - the
  // actions the Consolidated Fund form's Calls tab offers, so the call form
  // stays the single surface for a consolidated call (owner ruling
  // 2026-08-08). Compute shares the call by Consolidated Commitment (spec
  // A3.12); Issue books the amounts shown on the primary fund ledger. A
  // transfer-derived satellite call's lifecycle is the transfer's
  // (handleTransferBook & co below).
  const handleGroupIssue = useCallback(() => {
    if (!detail?.call?.id || !groupCallId) return
    const commitments = new Set(detail.per_commitment.map((pc) => pc.fund_commitment_id)).size
    modalApi.confirm({
      title: isGroupEqualization ? 'Issue equalization call?' : 'Issue consolidated call?',
      content: (
        <div>
          {isGroupEqualization ? (
            <p>
              This issues the catch-up lines as computed (each late closer's share of the earlier
              calls, weighed by subscription) and books the call on the primary fund ledger. The
              call becomes read-only; its name stays editable.
            </p>
          ) : (
            <p>
              This issues the call as computed (fee lines from the fee engine, everything else
              pro-rata by consolidated commitment) and books it on the primary fund ledger. The
              amounts shown are booked as they stand, including any you have edited. The call
              becomes read-only; its name stays editable.
            </p>
          )}
          <p className='mb-0'>
            <Text strong>{detail.call.fund_name}</Text>
            {' — '}
            {fmtMoney(detail.totals.called_total, currencySymbol)}
            {commitments ? ` across ${commitments} commitments` : ''}
          </p>
        </div>
      ),
      okText: 'Issue',
      onOk: async () => {
        setActionLoading(true)
        try {
          const res = await issueFundGroupCall(groupCallId, getAccessToken(store.getState().auth))
          if (!res.success) {
            showErrorToast(res.error || 'Failed to issue the consolidated call')
            void reloadDetail()
            return
          }
          toast.success('Consolidated call issued and booked')
          await reloadDetail()
        } finally {
          setActionLoading(false)
        }
      }
    })
  }, [detail, groupCallId, currencySymbol, modalApi, reloadDetail, isGroupEqualization])

  const handleGroupRevert = useCallback(() => {
    if (!detail?.call?.id || !groupCallId) return
    modalApi.confirm({
      title: 'Revert to Draft?',
      content:
        (groupCallDetail?.call?.prog__paid_total ?? 0) > 0
          ? `This unbooks the primary-fund capital call and returns it to an editable Draft. ${fmtMoney(groupCallDetail?.call?.prog__paid_total, currencySymbol)} of LP payments currently applied to it stay booked and read as prepayments until it is reissued.`
          : 'This unbooks the primary-fund capital call and returns it to an editable Draft. Payments already recorded are kept and read as prepayments until it is reissued.',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true)
        try {
          const res = await revertFundGroupCall(groupCallId, getAccessToken(store.getState().auth))
          if (!res.success) {
            showErrorToast(res.error || 'Failed to revert the consolidated call')
            return
          }
          toast.success('Consolidated call reverted to Draft')
          await reloadDetail()
        } finally {
          setActionLoading(false)
        }
      }
    })
  }, [detail, groupCallId, modalApi, reloadDetail, currencySymbol, groupCallDetail])

  const handleGroupDelete = useCallback(() => {
    if (!detail?.call?.id || !groupCallId) return
    const callId = detail.call.id
    modalApi.confirm({
      title: 'Delete draft consolidated call?',
      content: 'The consolidated plan and this draft primary-fund call are removed. Only Draft calls can be deleted.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await deleteFundGroupCall(groupCallId, getAccessToken(store.getState().auth))
        if (!res.success) {
          showErrorToast(res.error || 'Failed to delete the consolidated call')
          return
        }
        toast.success('Consolidated call deleted')
        dispatch(removeCapitalCall({ capitalCallId: callId }))
        navigate(LIST_PATH)
        dispatch(removeTab({ key: formTabKey }))
      }
    })
  }, [detail, groupCallId, dispatch, navigate, formTabKey, modalApi])

  // Intercompany transfer lifecycle (owner 2026-09-28): the derived
  // satellite call is edited here; booking issues it and books the
  // intercompany pair (fgc__book_transfer), Revert returns the whole
  // transfer to Draft, Delete trashes the Draft transfer with its call.
  const bookTransfer = useCallback(
    async (force: boolean) => {
      if (!transferId) return
      setActionLoading(true)
      try {
        const res = await bookFundGroupTransfer(transferId, force, getAccessToken(store.getState().auth))
        if (res.success) {
          toast.success('Transfer booked')
          await reloadDetail()
          return
        }
        if (res.overCalledWarning) {
          modalApi.confirm({
            title: 'Transfer exceeds the capital called',
            content: res.overCalledWarning.message,
            okText: 'Book anyway',
            okButtonProps: { danger: true },
            onOk: () => bookTransfer(true)
          })
          return
        }
        showErrorToast(res.error || 'Failed to book the transfer')
        void reloadDetail()
      } finally {
        setActionLoading(false)
      }
    },
    [transferId, modalApi, reloadDetail]
  )

  const handleTransferBook = useCallback(() => {
    if (!detail?.call?.id || !transferId) return
    const commitments = new Set(detail.per_commitment.map((pc) => pc.fund_commitment_id)).size
    modalApi.confirm({
      title: 'Book this transfer?',
      content: (
        <div>
          <p>
            The amounts shown are booked as they stand: this call is issued on{' '}
            {detail.call.fund_name}, the satellite's contribution records are
            written and the intercompany pair is booked on both funds. Revert to Draft undoes it.
          </p>
          <p className='mb-0'>
            <Text strong>
              {transferContext?.primary_fund_name ?? 'Primary'} → {detail.call.fund_name}
            </Text>
            {' — '}
            {fmtMoney(detail.totals.called_total, currencySymbol)}
            {commitments ? ` across ${commitments} investors` : ''}
          </p>
        </div>
      ),
      okText: 'Book',
      onOk: () => bookTransfer(false)
    })
  }, [detail, transferId, transferContext, currencySymbol, modalApi, bookTransfer])

  const handleTransferRevert = useCallback(() => {
    if (!transferId) return
    modalApi.confirm({
      title: 'Revert transfer to Draft?',
      content:
        'This unbooks and removes the intercompany pair and the satellite contribution records, and returns the transfer and this call to an editable Draft. Blocked only when the journals sit in a closed financial year.',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true)
        try {
          const res = await revertFundGroupTransfer(transferId, getAccessToken(store.getState().auth))
          if (!res.success) {
            showErrorToast(res.error || 'Failed to revert the transfer')
            return
          }
          toast.success('Transfer reverted to Draft')
          await reloadDetail()
        } finally {
          setActionLoading(false)
        }
      }
    })
  }, [transferId, modalApi, reloadDetail])

  const handleTransferDelete = useCallback(() => {
    if (!detail?.call?.id || !transferId) return
    const callId = detail.call.id
    modalApi.confirm({
      title: 'Delete draft transfer?',
      content: 'The draft transfer and this satellite call are moved to trash.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await deleteFundGroupTransfer(transferId, getAccessToken(store.getState().auth))
        if (!res.success) {
          showErrorToast(res.error || 'Failed to delete the transfer')
          return
        }
        toast.success('Draft transfer deleted')
        dispatch(removeCapitalCall({ capitalCallId: callId }))
        dispatch(removeTab({ key: formTabKey }))
      }
    })
  }, [detail, transferId, dispatch, formTabKey, modalApi])

  // Lifecycle buttons: own orchestrators for a plain call, the fgc__ ones
  // for the gross consolidated call and for a transfer-derived call.
  const canRunLifecycle = !isGroupLinked || isGroupCallLinked || (isTransferCall && Boolean(transferId))
  const onIssue = isTransferCall ? handleTransferBook : isGroupCallLinked ? handleGroupIssue : handleIssue
  const onRevert = isTransferCall ? handleTransferRevert : isGroupCallLinked ? handleGroupRevert : handleRevert
  const onDelete = isTransferCall ? handleTransferDelete : isGroupCallLinked ? handleGroupDelete : handleDelete

  // ----- what stands between a Draft and Issue (spec A3.12 part 2) -----
  // Issue books the amounts shown, so it is refused while they do not match
  // the inputs (capital_calls.needs_compute: an input changed since the
  // last Compute, or the call was never computed). The button is disabled
  // with the reason and, where amounts are already on screen, an alert says
  // what to do - the server's own refusal is the backstop, not the message.
  const issueLabel = isTransferCall ? 'Book transfer' : 'Issue'
  const hasAmounts = (detail?.per_commitment ?? []).length > 0
  const needsCompute = Boolean(detail?.call?.id) && !isIssued && detail?.call?.needs_compute === true
  // amounts computed before the calculation was kept with the call (a Draft
  // drawn up before spec A3.12): nothing changed, but there is no record
  const computedBeforeRecord = needsCompute && hasAmounts && !detail?.call?.computed_at
  const issueBlockedReason: string | null = dirty
    ? 'Save your changes first.'
    : items.length + feeItems.length === 0
      ? 'Add a line first.'
      : needsCompute
        ? hasAmounts
          ? computedBeforeRecord
            ? 'Recompute first: these amounts were computed before the calculation was kept with the call.'
            : 'Recompute first: the inputs changed since the amounts were computed.'
          : 'Compute the amounts first.'
        : null
  // a computed Draft: what differs today from the record it was computed on
  const changedSinceCompute = !isIssued ? detail?.call?.prog__changed_since_compute ?? [] : []
  // a reverted Draft whose amounts still stand as issued (unsaved edits in
  // the form are not what was issued, so the banner waits for a clean form)
  const asIssuedAt = !isIssued && !dirty ? detail?.call?.prog__as_issued_at ?? null : null
  const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)
  // ----- renderers -----

  // live "Calculated" per the notebook: amount rows echo the amount, % rows
  // preview pct x closed-commitment pool; fee rows show the engine result;
  // balance rows preview call total - sum of sibling effectives (floored 0)
  const closedPool = detail?.call?.prog__closed_commitments_total ?? null
  const clientCallTotal = useMemo(() => {
    if (totalMode === 'percent')
      return totalPercentage != null && closedPool != null
        ? Math.round(totalPercentage * closedPool * 100) / 100
        : null
    if (totalMode === 'amount') return totalAmount
    return null
  }, [totalMode, totalPercentage, totalAmount, closedPool])

  // ----- late-closer catch-up context (design Q4 Option A) -----
  // The equalization preview supplies late-closer badges for the pivot and
  // gates the Add catch-up lines action; it reads only issued calls, so a
  // Draft's own items never feed back into it.
  const [eqPreview, setEqPreview] = useState<EqualizationPreview | null>(null)
  useEffect(() => {
    let cancelled = false
    if (!fundId) {
      setEqPreview(null)
      return
    }
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundEqualizationPreview(fundId, token)
      if (!cancelled) setEqPreview(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [fundId])

  const lateCloserCommitmentIds = useMemo(
    () =>
      new Set(
        (eqPreview?.data ?? [])
          .filter((r) => r.prog__is_late_closer)
          .map((r) => r.fund_commitments__id)
      ),
    [eqPreview]
  )
  const catchupOutstanding =
    eqPreview?.late_closer_policy === 'Full catch-up' &&
    (Math.abs(eqPreview?.totals?.fee_catchup_total ?? 0) >= 0.005 ||
      Math.abs(eqPreview?.totals?.equalization_charge_total ?? 0) >= 0.005)
  const hasCatchupItems = items.some((r) => r.catchup_kind_id != null || r.catchup_kind_label)

  const addCatchupLines = () => {
    const feeKind = catchupKinds.find((k: any) => k.label === 'Fee catch-up')
    const eqKind = catchupKinds.find((k: any) => k.label === 'Contribution equalization')
    const topupKind = catchupKinds.find((k: any) => k.label === 'Contribution top-up')
    const feeItemType = accItems.find(
      (i: any) => i.label === 'Capital contribution - fees' && i.call_component
    )
    if (!feeKind || !eqKind || !topupKind || !feeItemType) {
      showErrorToast('Catch-up kinds / call components are not configured')
      return
    }
    const newRows: ItemRow[] = []
    if (Math.abs(eqPreview?.totals?.fee_catchup_total ?? 0) >= 0.005) {
      newRows.push({
        ...emptyItemRow(),
        item_type_id: feeItemType.id,
        description: 'Fee catch-up',
        catchup_kind_id: feeKind.id,
        catchup_kind_label: feeKind.label
      })
    }
    // one contribution catch-up row per (source item type x spec) group, the
    // wizard's defaults: rate groups top up, fixed groups reallocate. A type
    // with BOTH specs collapses to one row scoped (type, all specs) in
    // reallocate mode (per-commitment rows key on item type). The Mode
    // select on each row can deviate before saving.
    const byType = new Map<string, { label: string; specs: Set<string> }>()
    for (const g of eqPreview?.groups ?? []) {
      if (!g.item_type_id || g.item_type_id === feeItemType.id) continue
      const entry = byType.get(g.item_type_id) ?? {
        label: g.item_type_label ?? 'Contribution',
        specs: new Set<string>()
      }
      if (g.spec) entry.specs.add(g.spec)
      byType.set(g.item_type_id, entry)
    }
    for (const [typeId, entry] of byType) {
      const spec = entry.specs.size === 1 ? [...entry.specs][0] : null
      const kind = spec === 'rate' ? topupKind : eqKind
      newRows.push({
        ...emptyItemRow(),
        item_type_id: typeId,
        description: `${entry.label} ${kind.id === topupKind.id ? 'top-up' : 'equalization'}`,
        catchup_kind_id: kind.id,
        catchup_kind_label: kind.label,
        catchup_source_item_type_id: typeId,
        catchup_source_spec: spec
      })
    }
    if (newRows.length === 0) return
    setItems((prev) => [...prev.filter((r) => r.item_type_id || r.description), ...newRows])
    markDirty()
  }

  const nonBalanceCalculated = (row: ItemRow): number | null => {
    if (row.mgmt_fee_period_from || row.mgmt_fee_period_to) return row.calculated_amount
    // catch-up rows are engine-derived from the issued ledger, never typed
    if (row.catchup_kind_id != null || row.catchup_kind_label) return row.calculated_amount
    if (row.input_type === 'amount') return row.amount
    if (row.percentage == null || closedPool == null) return null
    return Math.round(row.percentage * closedPool * 100) / 100
  }
  // Exact gross (owner 2026-09-28): a set gross amount is hit exactly - the
  // balance nets the EFFECTIVE fees, absorbing the side-letter breaks. A %
  // of committed (or % of subscriptions) call, and a call issued under the
  // earlier rule (gross_amount_exact false), nets the HEADLINE fees instead.
  // A Draft adopts the exact rule on its next compute. Mirrors
  // capital_calls__item_effective_amount.
  const grossExact =
    totalMode === 'amount' &&
    (isGroupSized
      ? groupSizing.sizingMode === 'amount'
      : isIssued
        ? Boolean(detail?.call?.gross_amount_exact)
        : true)
  // Fees / expenses outside the commitment (spec A3.9 addendum part 4): on
  // a % of committed call the lines that do not count toward the commitment
  // (the fund's commitment_includes_* terms) sit OUTSIDE the % - the balance
  // nets only the siblings that count and the others come on top. A set
  // amount is unchanged - except for the expenses line, which follows the
  // call's "Include expenses" choice once made. Mirrors
  // capital_calls__item_in_total; the terms and the bucket items come with
  // the call (prog__commitment_terms).
  const commitmentTerms = (detail?.call?.prog__commitment_terms ?? null) as CommitmentTerms | null
  const pctSized = isGroupSized ? groupSizing.sizingMode === 'percentage' : totalMode === 'percent'
  const countsTowardCommitment = (r: ItemRow): boolean =>
    bucketCountsTowardCommitment(
      callLineBucket(
        { item_type_id: r.item_type_id, isFeePeriodLine: Boolean(r.mgmt_fee_period_from || r.mgmt_fee_period_to) },
        commitmentTerms
      ),
      commitmentTerms
    )
  // "Include management fees" / "Include expenses" (capital_calls__
  // item_in_total): the fee lines and the expenses line follow the call's
  // choice; unset, the rule every other line follows - on top only when a %
  // of committed call's fund keeps them outside the commitment
  const feesInTotalEffective =
    feesInTotal ?? !(pctSized && commitmentTerms?.includes_mgmt_fees === false)
  const expensesInTotalEffective =
    expensesInTotal ?? !(pctSized && commitmentTerms?.includes_expenses === false)
  // the expenses item before the first save (the terms come with the call)
  const expensesLineTypeId = commitmentTerms?.expenses_item_type_id ?? expensesTypeId
  const lineBucket = (r: ItemRow): CallLineBucket =>
    callLineBucket(
      { item_type_id: r.item_type_id, isFeePeriodLine: Boolean(r.mgmt_fee_period_from || r.mgmt_fee_period_to) },
      { ...commitmentTerms, expenses_item_type_id: expensesLineTypeId }
    )
  const onTopOfTotal = (r: ItemRow): boolean => {
    if (r.is_balance) return false
    const bucket = lineBucket(r)
    if (bucket === 'fees') return !feesInTotalEffective
    if (bucket === 'expenses') return !expensesInTotalEffective
    return pctSized && !countsTowardCommitment(r)
  }
  const nonBalanceSum = useMemo(
    () =>
      [...items, ...feeItems]
        .filter((r) => !r.is_balance && !onTopOfTotal(r))
        .reduce(
          (acc, r) =>
            acc +
            // a fee line counts at its HEADLINE unless the gross is exact:
            // either way each LP's side-letter break comes off that LP's
            // own balance share (owner rulings 2026-09-26 / 2026-09-28)
            (!grossExact && (r.mgmt_fee_period_from || r.mgmt_fee_period_to) && r.headline_total != null
              ? r.headline_total
              : (r.override_amount ?? nonBalanceCalculated(r) ?? 0)),
          0
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, feeItems, closedPool, grossExact, pctSized, commitmentTerms, feesInTotalEffective, expensesInTotalEffective]
  )
  // what comes on top of the call amount
  const onTopSum = useMemo(
    () =>
      [...items, ...feeItems]
        .filter((r) => onTopOfTotal(r))
        .reduce((acc, r) => acc + (r.override_amount ?? nonBalanceCalculated(r) ?? 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, feeItems, closedPool, pctSized, commitmentTerms, feesInTotalEffective, expensesInTotalEffective]
  )
  // the line types added on top of the call amount, in words
  const onTopLabel = outsideCommitmentLabel(feesInTotalEffective, expensesInTotalEffective)
  // where the call amount is, for the switches and the Totals note
  const callAmountText = !pctSized
    ? 'set amount'
    : isGroupSized
    ? `${groupSizing.totalPercentage ?? 0}% of consolidated commitments`
    : totalPercentage != null
    ? `${decimalToPercent(totalPercentage)}% of committed`
    : '% of committed'
  // a fund whose expenses sit outside the commitment should call them on
  // the expenses item: the combined investments-and-expenses item cannot be
  // split, so all of it counts toward the commitment
  const combinedItemInUse =
    commitmentTerms?.includes_expenses === false &&
    commitmentTerms?.combined_item_type_id != null &&
    items.some((r) => r.item_type_id === commitmentTerms.combined_item_type_id)
  const itemLabel = (id: string | null | undefined): string =>
    itemTypeOptions.find((o: { value: string; label: string }) => o.value === id)?.label ?? 'that item'
  const overTotal = clientCallTotal != null && nonBalanceSum > clientCallTotal
  const liveCalculated = (row: ItemRow): number | null => {
    if (!row.is_balance) return nonBalanceCalculated(row)
    if (clientCallTotal == null) return row.effective_amount
    return Math.max(0, Math.round((clientCallTotal - nonBalanceSum) * 100) / 100)
  }

  const itemColumns = [
    {
      title: 'Item',
      dataIndex: 'item_type_id',
      width: 210,
      render: (_: unknown, row: ItemRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.item_type_id}
          options={itemTypeOptions}
          placeholder='Select item...'
          showSearch
          optionFilterProp='label'
          disabled={readOnly}
          onChange={(v) => updateItem(row.key, { item_type_id: v })}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 200,
      render: (_: unknown, row: ItemRow) => (
        <Input
          size='small'
          value={row.description}
          disabled={readOnly}
          onChange={(e) => updateItem(row.key, { description: e.target.value })}
        />
      )
    },
    {
      title: 'Type',
      dataIndex: 'input_type',
      width: 100,
      render: (_: unknown, row: ItemRow) =>
        row.catchup_kind_label === 'Fee catch-up' ? (
          <Tag color='purple'>Fee catch-up</Tag>
        ) : row.catchup_kind_label ? (
          // contribution catch-up: the mode is a per-line choice (owner
          // decision 2026-08-04) - swapping the kind swaps the calculator
          <Select
            size='small'
            className='w-full'
            value={row.catchup_kind_id}
            options={catchupKinds
              .filter((k: any) => k.label !== 'Fee catch-up' && k.label !== 'Catch-up interest')
              .map((k: any) => ({
                value: k.id,
                label: k.label === 'Contribution top-up' ? 'Top-up' : 'Reallocate'
              }))}
            disabled={readOnly}
            onChange={(v) => {
              const kind = catchupKinds.find((k: any) => k.id === v)
              updateItem(row.key, {
                catchup_kind_id: v as number,
                catchup_kind_label: kind?.label ?? null
              })
            }}
          />
        ) : row.is_balance ? (
          <Tag color='purple'>Balance</Tag>
        ) : (
          <Select
            size='small'
            className='w-full'
            value={row.input_type}
            options={[
              { value: 'amount', label: 'Amount' },
              { value: 'percent', label: '%' }
            ]}
            disabled={readOnly}
            onChange={(v) =>
              updateItem(row.key, {
                input_type: v as 'amount' | 'percent',
                // the toggle makes the inputs mutually exclusive
                ...(v === 'amount' ? { percentage: null } : { amount: null })
              })
            }
          />
        )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) =>
        row.is_balance || row.catchup_kind_label ? (
          <Text type='secondary'>—</Text>
        ) : (
          <InputNumber
            size='small'
            className='w-full'
            value={row.amount}
            {...currencyFmt}
            disabled={readOnly || row.input_type !== 'amount'}
            onChange={(v) => updateItem(row.key, { amount: v == null ? null : Number(v) })}
          />
        )
    },
    {
      title: '% of Committed',
      dataIndex: 'percentage',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) =>
        row.is_balance || row.catchup_kind_label ? (
          <Text type='secondary'>—</Text>
        ) : (
          <InputNumber
            size='small'
            className='w-full'
            // stored as a decimal (0.05), edited as a percentage (5)
            value={decimalToPercent(row.percentage)}
            precision={4}
            suffix='%'
            disabled={readOnly || row.input_type !== 'percent'}
            onChange={(v) => updateItem(row.key, { percentage: percentToDecimal(v) })}
          />
        )
    },
    {
      title: 'Calculated',
      dataIndex: 'calculated_amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) => {
        const calc = liveCalculated(row)
        return currencyText(calc)
      }
    },
    {
      title: 'Override',
      dataIndex: 'override_amount',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) => (
        <InputNumber
          size='small'
          className='w-full'
          value={row.override_amount}
          {...currencyFmt}
          placeholder='-'
          disabled={readOnly}
          onChange={(v) => updateItem(row.key, { override_amount: v == null ? null : Number(v) })}
        />
      )
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: ItemRow) =>
        readOnly ? null : (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(row.key)}
          />
        )
    }
  ]

  const feeColumns = [
    {
      title: 'Item',
      dataIndex: 'item_type_id',
      width: 210,
      render: (_: unknown, row: ItemRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.item_type_id}
          options={itemTypeOptions}
          placeholder='Select item...'
          showSearch
          optionFilterProp='label'
          disabled={readOnly}
          onChange={(v) => updateFeeItem(row.key, { item_type_id: v })}
        />
      )
    },
    {
      title: 'Period From',
      dataIndex: 'mgmt_fee_period_from',
      width: 130,
      render: (_: unknown, row: ItemRow) => (
        <DatePicker
          size='small'
          className='w-full'
          value={row.mgmt_fee_period_from ? dayjs(row.mgmt_fee_period_from) : null}
          format={DATE_FORMAT}
          placeholder='From'
          disabled={readOnly}
          onChange={(d) => updateFeeItem(row.key, { mgmt_fee_period_from: d ? d.format('YYYY-MM-DD') : null })}
        />
      )
    },
    {
      title: 'Period To',
      dataIndex: 'mgmt_fee_period_to',
      width: 130,
      render: (_: unknown, row: ItemRow) => (
        <DatePicker
          size='small'
          className='w-full'
          value={row.mgmt_fee_period_to ? dayjs(row.mgmt_fee_period_to) : null}
          format={DATE_FORMAT}
          placeholder='To'
          disabled={readOnly}
          onChange={(d) => updateFeeItem(row.key, { mgmt_fee_period_to: d ? d.format('YYYY-MM-DD') : null })}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (_: unknown, row: ItemRow) => (
        <Input
          size='small'
          value={row.description}
          placeholder='Auto-filled with the period on compute'
          disabled={readOnly}
          onChange={(e) => updateFeeItem(row.key, { description: e.target.value })}
        />
      )
    },
    {
      title: 'Calculated',
      dataIndex: 'calculated_amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) =>
        currencyText(row.calculated_amount)
    },
    {
      title: 'Override',
      dataIndex: 'override_amount',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) => (
        <InputNumber
          size='small'
          className='w-full'
          value={row.override_amount}
          {...currencyFmt}
          placeholder='-'
          disabled={readOnly}
          onChange={(v) => updateFeeItem(row.key, { override_amount: v == null ? null : Number(v) })}
        />
      )
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: ItemRow) =>
        readOnly ? null : (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeFeeItem(row.key)}
          />
        )
    }
  ]

  // notebook Totals: Fund Input (effective incl. override) vs Investor
  // Aggregate (sum of per-commitment effective amounts)
  const totalsRows = useMemo(() => {
    const label = (row: ItemRow) =>
      itemTypeOptions.find((o: any) => o.value === row.item_type_id)?.label ?? row.description ?? 'Item'
    const allRows = [...items, ...feeItems]
    return allRows.map((row) => ({
      key: row.key,
      label: label(row),
      fundInput: row.override_amount ?? liveCalculated(row),
      investorAggregate: row.per_commitment_total ?? null
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, feeItems, itemTypeOptions, closedPool])

  const totalsSum = useMemo(
    () => ({
      fundInput: totalsRows.reduce((acc, r) => acc + (r.fundInput ?? 0), 0),
      investorAggregate: totalsRows.reduce((acc, r) => acc + (r.investorAggregate ?? 0), 0)
    }),
    [totalsRows]
  )

  // Per-commitment PIVOT: one row per commitment, per live item type a
  // Calculated and an Override column (splits), plus the row total — "for
  // each commitment, the total as well as the splits". The Override input
  // edits the per-commitment override; clearing it falls back to the
  // calculated amount (spec A3.12 part 2).
  const pivotItemTypes = useMemo(() => {
    const seen: AllocationItemType[] = []
    for (const r of [...items, ...feeItems]) {
      if (r.item_type_id && !seen.some((s) => s.id === r.item_type_id)) {
        // a fee line's header carries its rate - where one fee period is
        // called on the item (the per-commitment rows key on the item type,
        // so two periods on one item have no single rate to show)
        const feeRows = feeItems.filter((f) => f.item_type_id === r.item_type_id)
        seen.push({
          id: r.item_type_id,
          label: itemTypeOptions.find((o: any) => o.value === r.item_type_id)?.label ?? 'Item',
          isFee: feeRows.length > 0,
          feeRate: feeRows.length === 1 ? feeRows[0].called_rate : null
        })
      }
    }
    return seen
  }, [items, feeItems, itemTypeOptions])

  // What the call is sized on (spec A3.9 addendum part 2): the primary-fund
  // call of a consolidated call is sized on each investor's Consolidated
  // Commitment, a transfer-derived call on the satellite commitment, a
  // standalone call on its own commitment - the allocation table's
  // commitment columns (and its Excel export) follow.
  const consolidatedBasis = (detail?.call?.prog__consolidated_basis ?? null) as ConsolidatedBasis
  const allocLayout = useMemo(
    () => allocationLayout(consolidatedBasis, detail?.call?.fund_name),
    [consolidatedBasis, detail]
  )

  const perCommitmentRows = useMemo(() => {
    const byCommitment = new Map<string, AllocationRow>()
    for (const pc of detail?.per_commitment ?? []) {
      let row = byCommitment.get(pc.fund_commitment_id)
      if (!row) {
        row = {
          key: pc.fund_commitment_id,
          investor: pc.investors__name ?? null,
          commitment: pc.fund_commitments__amount ?? null,
          consolidatedCommitment: pc.fund_group_commitments__amount ?? null,
          cells: {}
        }
        byCommitment.set(pc.fund_commitment_id, row)
      }
      row.cells[pc.item_type_id] = pc as AllocationCell
    }
    return sortAllocationRows([...byCommitment.values()], consolidatedBasis)
  }, [detail, consolidatedBasis])

  // any side-letter fee break on the call: the per-investor table then
  // shows what came off each investor's own call (owner ruling 2026-09-26)
  const hasFeeBreaks = useMemo(
    () => (detail?.per_commitment ?? []).some((pc) => (pc.prog__fee_break ?? 0) > 0),
    [detail]
  )
  // spec A3.9: when investors contribute in full the break comes off
  // nobody's call - it is a waived fee (settled per the fund's Waived Fee
  // Settlement)
  const feeBreakLabel = detail?.totals?.fee_break_in_full ? 'Waived fee' : 'Fee break'
  const rowFeeBreak = useCallback(
    (row: AllocationRow): number =>
      Math.round(
        Object.values(row.cells).reduce((acc: number, pc) => acc + (pc?.prog__fee_break ?? 0), 0) * 100
      ) / 100,
    []
  )

  const handleExportCall = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    // the table's columns in the table's order (allocationExport shares the
    // layout with CallAllocationTable)
    const { data, columns, percentageColumns } = allocationExport({
      rows: perCommitmentRows,
      layout: allocLayout,
      itemTypes: pivotItemTypes,
      pcOverride,
      hasFeeBreaks,
      feeBreakLabel,
      rowFeeBreak
    })
    const res = await exportJsonToExcel(
      {
        data,
        columns,
        filename: `capital_call_${detail?.call?.name || 'draft'}`,
        ...(percentageColumns.length > 0 ? { percentageColumns } : {})
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [perCommitmentRows, allocLayout, pivotItemTypes, pcOverride, detail, hasFeeBreaks, feeBreakLabel, rowFeeBreak])

  const linkedTransactionCard =
    detail?.transaction?.id != null ? (
      <FormSection title='Linked Fund Transaction' titleColor='gray' scrollable={false}>
        <Space size='large' wrap>
          <span>
            Status: <Tag color={detail.transaction.status_label === 'Booked' ? 'green' : 'blue'}>{detail.transaction.status_label}</Tag>
          </span>
          <span>Journals: {detail.transaction.journal_count ?? 0}</span>
          <span>Total: {fmtMoney(detail.transaction.total_debits, currencySymbol)}</span>
          <Button
            size='small'
            onClick={() =>
              openFormInTab(
                TABLE_FUND_TRANSACTIONS,
                detail.transaction!.id,
                `Capital Call — ${detail.call.fund_name ?? ''}`
              )
            }
          >
            Open Transaction
          </Button>
        </Space>
      </FormSection>
    ) : null

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className='p-10 text-center text-gray-500'>
        Capital call not found.
        <div className='mt-3'>
          <Button onClick={() => navigate(LIST_PATH)}>Back to Capital Calls</Button>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full w-full min-h-0 flex flex-col bg-gray-50 p-3 overflow-hidden'>
      {modalContextHolder}
      <RecomputeEditsModal
        open={recomputeOpen}
        edits={manualEdits}
        currencyText={currencyText}
        onCancel={() => setRecomputeOpen(false)}
        onKeep={() => {
          setRecomputeOpen(false)
          void runCompute('keep')
        }}
        onDiscard={() => {
          setRecomputeOpen(false)
          void runCompute('discard')
        }}
      />
      <Drawer
        title='Record payments'
        width={1100}
        open={paymentsOpen}
        destroyOnHidden
        onClose={() => setPaymentsOpen(false)}
      >
        <Alert
          className='mb-3'
          type='info'
          showIcon
          message='Payments are fund-wide and applied to the oldest outstanding issued call first — not to this call alone.'
        />
        <PaymentEntryGrid fundId={detail?.call.fund_id ?? null} withSection={false} />
      </Drawer>
      <Card size='small' className='flex-1 min-h-0 flex flex-col h-full overflow-hidden' classNames={{ body: 'h-full p-3 overflow-y-auto' }}>
        <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
          <Space align='center'>
            <Title level={5} className='!mb-0'>
              {isNewRecord()
                ? isNewConsolidated
                  ? 'New Consolidated Call'
                  : 'New Capital Call'
                : `${detail?.call.name || 'Capital Call'} — ${detail?.call.fund_name ?? ''}`}
            </Title>
            <Tag color={isIssued ? 'green' : 'blue'}>{statusLabel}</Tag>
          </Space>
          <Space>
            {/* Owner spec: primary rightmost; destructive separated leftmost. */}
            {!isNewRecord() && !isIssued && canRunLifecycle ? (
              <Button danger onClick={onDelete}>
                Delete
              </Button>
            ) : null}
            {isIssued && !isTransferCall ? (
              <Button icon={<DollarOutlined />} onClick={() => setPaymentsOpen(true)}>
                Record payments
              </Button>
            ) : null}
            {!isNewRecord() ? (
              <Button
                icon={<DownloadOutlined />}
                disabled={perCommitmentRows.length === 0}
                onClick={handleExportCall}
              >
                Export
              </Button>
            ) : null}
            {isIssued && canRunLifecycle ? (
              <Button loading={actionLoading} onClick={onRevert}>
                Revert to Draft
              </Button>
            ) : null}
            {!isNewRecord() && !isIssued ? (
              <Button loading={actionLoading || saving} onClick={handleCompute}>
                {perCommitmentRows.length > 0 ? 'Recompute' : 'Compute'}
              </Button>
            ) : null}
            {!isNewRecord() && !isIssued && canRunLifecycle ? (
              // a blocked step is disabled with its reason (the span keeps
              // the tooltip alive over a disabled button)
              <Tooltip title={issueBlockedReason}>
                <span>
                  <Button
                    type='primary'
                    ghost
                    loading={actionLoading}
                    disabled={issueBlockedReason != null}
                    onClick={onIssue}
                  >
                    {issueLabel}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {!readOnly ? (
              <Button type='primary' loading={saving} onClick={handleSave}>
                {isNewRecord() ? 'Create' : 'Save'}
              </Button>
            ) : (
              <Button type='primary' loading={saving} disabled={!renamePending} onClick={handleRename}>
                Save
              </Button>
            )}
          </Space>
        </div>

        <div className='flex flex-col gap-3'>
          {isGroupSized || isNewConsolidated ? (
            <ConsolidatedCallBanner
              mode={isNewConsolidated ? 'new' : 'linked'}
              groupName={consolidatedGroupName}
              editable={!readOnly && !saving}
              value={groupSizing}
              onChange={(patch) => {
                setGroupSizing((prev) => ({ ...prev, ...patch }))
                markDirty()
              }}
              itemTypeOptions={itemTypeOptions}
              pool={
                isNewConsolidated
                  ? groupPool.pool
                  : (groupCallDetail?.call?.prog__closed_subscription_pool as number | null | undefined) ?? null
              }
              poolFmt={
                isNewConsolidated
                  ? groupPool.poolFmt
                  : (groupCallDetail?.call?.prog__closed_subscription_pool_fmt as string | null | undefined) ?? null
              }
              grossFmt={(groupCallDetail?.call?.prog__gross_total_fmt as string | null | undefined) ?? null}
              currencySymbol={
                currencySymbol ??
                (groupCallDetail?.call?.currency_symbol as string | null | undefined) ??
                null
              }
            />
          ) : isGroupEqualization ? (
            <Alert
              type='info'
              showIcon
              message={`Equalization call of ${consolidatedGroupName ?? 'the consolidated fund'}`}
              description='The catch-up lines size this call per late closer. It lists on the Consolidated Fund form (Capital Calls tab), and once issued it can fund intercompany transfers to the satellite funds.'
            />
          ) : transferContext ? (
            <TransferCallBanner transfer={transferContext} />
          ) : isGroupLinked ? (
            <Alert
              type='info'
              showIcon
              message='Part of a consolidated call'
              description='This call was derived by an intercompany transfer. Its lifecycle runs on the Consolidated Fund form (Capital Calls tab).'
            />
          ) : null}
          {/* the state of the amounts (spec A3.12 part 2): as issued after a
              revert, out of date against the inputs, or computed on terms
              that have moved since. Each says what happened and names the
              button that deals with it. */}
          {asIssuedAt ? (
            <Alert
              type='info'
              showIcon
              message={`Reverted to Draft: the amounts are as issued on ${dayjs(asIssuedAt).format(DATE_FORMAT)}`}
              description={`${issueLabel} books them again unchanged. Recompute recalculates them from the inputs.`}
            />
          ) : null}
          {needsCompute && hasAmounts ? (
            <Alert
              type='warning'
              showIcon
              message={isTransferCall ? 'Recompute before booking the transfer' : 'Recompute before issuing'}
              description={
                computedBeforeRecord
                  ? `The amounts per investor were computed before the calculation was kept with the call. Press Recompute once, check the amounts, then ${issueLabel}.`
                  : `The inputs of this call changed since its amounts were computed, so the amounts per investor no longer match them. Press Recompute, check the amounts, then ${issueLabel}.`
              }
            />
          ) : null}
          {changedSinceCompute.length > 0 ? (
            <Alert
              type='warning'
              showIcon
              message='Changed since the amounts were computed'
              description={
                <>
                  <ul className='list-disc pl-5 mb-1'>
                    {changedSinceCompute.map((change) => (
                      <li key={change.code}>{capitalise(change.message)}</li>
                    ))}
                  </ul>
                  <div>Recompute to apply. Until then {issueLabel} books the amounts shown.</div>
                </>
              }
            />
          ) : null}
          <FormSection title='Details' titleColor='blue' scrollable={false}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Fund</div>
                <FundSelect
                  className='w-full'
                  value={fundId}
                  placeholder='Select fund...'
                  disabled={readOnly || !isNewRecord()}
                  onChange={(v) => {
                    setFundId(v)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={24} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Name</div>
                <Input
                  value={name}
                  placeholder='e.g. Capital Call 3'
                  disabled={saving}
                  onChange={(e) => {
                    setName(e.target.value)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={12} md={4}>
                <div className='mb-1 text-gray-500 text-sm'>Call Date</div>
                <DatePicker
                  className='w-full'
                  value={callDate ? dayjs(callDate) : null}
                  format={DATE_FORMAT}
                  disabled={readOnly}
                  onChange={(d) => {
                    setCallDate(d ? d.format('YYYY-MM-DD') : null)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={12} md={4}>
                <div className='mb-1 text-gray-500 text-sm'>Due Date</div>
                <DatePicker
                  className='w-full'
                  value={dueDate ? dayjs(dueDate) : null}
                  format={DATE_FORMAT}
                  disabled={readOnly}
                  onChange={(d) => {
                    setDueDate(d ? d.format('YYYY-MM-DD') : null)
                    markDirty()
                  }}
                />
              </Col>
            </Row>
            {/* the LPA's notice period, checked against the SAVED dates; a
                transfer-derived call sends no notices */}
            {!dirty && !isTransferCall ? (
              <NoticePeriodAlert capitalCallId={detail?.call?.id} version={detail} className='mt-3' />
            ) : null}
          </FormSection>

          {/* new-consolidated mode: only Details + banner render — fee and
              expense lines are added after the first save materialises the
              call (fgc__save_call owns creation) */}
          {isNewConsolidated ? null : (
          <>
          <FormSection title='Call Amount' titleColor='blue' scrollable={false}>
            {isGroupSized ? (
              // the gross sizing of a consolidated call lives in the banner
              // (fgc__save_call resolves % against the SUBSCRIPTION pool);
              // the native % control's pool is pocket commitments — wrong
              <Text type='secondary'>
                Gross sizing lives in the consolidated banner above — the materialised call always
                carries the resolved fixed amount ({fmtMoney(totalAmount, currencySymbol)}).
              </Text>
            ) : (
            <Space wrap align='center'>
              <Segmented
                value={totalMode}
                disabled={readOnly}
                options={[
                  { label: 'No total', value: 'none' },
                  { label: '% of committed', value: 'percent' },
                  { label: 'Set amount', value: 'amount' }
                ]}
                onChange={(v) => handleTotalModeChange(v as 'none' | 'percent' | 'amount')}
              />
              {totalMode === 'percent' ? (
                <InputNumber
                  className='w-36'
                  value={decimalToPercent(totalPercentage)}
                  precision={4}
                  suffix='%'
                  disabled={readOnly}
                  onChange={(v) => {
                    setTotalPercentage(percentToDecimal(v))
                    markDirty()
                  }}
                />
              ) : null}
              {totalMode === 'amount' ? (
                <InputNumber
                  className='w-44'
                  value={totalAmount}
                  {...currencyFmt}
                  disabled={readOnly}
                  onChange={(v) => {
                    setTotalAmount(v == null ? null : Number(v))
                    markDirty()
                  }}
                />
              ) : null}
              {totalMode !== 'none' && clientCallTotal != null ? (
                <Text type='secondary'>
                  = {fmtMoney(clientCallTotal, currencySymbol)}
                  {closedPool != null ? ` on ${fmtMoney(closedPool, currencySymbol)} closed commitments` : ''}
                </Text>
              ) : null}
            </Space>
            )}
            {/* a consolidated call's sizing lives in its banner; without a
                call amount there is nothing to be inside or on top of */}
            {!isGroupSized && totalMode !== 'none' ? (
              <div className='mt-3 flex flex-col gap-2'>
                <Space>
                  <Switch
                    size='small'
                    checked={feesInTotalEffective}
                    disabled={readOnly}
                    onChange={handleFeesInTotalChange}
                  />
                  <Text>Include management fees</Text>
                  <Text type='secondary'>
                    — {feesInTotalEffective ? 'the fees are part of the' : 'the fees are added on top of the'}{' '}
                    {callAmountText}; add a fee period under Management Fees below
                  </Text>
                </Space>
                <Space>
                  <Switch
                    size='small'
                    checked={expensesInTotalEffective}
                    disabled={readOnly}
                    onChange={handleExpensesInTotalChange}
                  />
                  <Text>Include expenses</Text>
                  <Text type='secondary'>
                    — {expensesInTotalEffective ? 'the expenses are part of the' : 'the expenses are added on top of the'}{' '}
                    {callAmountText}; call them on a &quot;{itemLabel(expensesLineTypeId)}&quot; line below
                  </Text>
                </Space>
              </div>
            ) : null}
            {overTotal ? (
              <Alert
                className='mt-3'
                type='warning'
                showIcon
                message={`Fees and other items (${fmtMoney(nonBalanceSum, currencySymbol)}) exceed the call total (${fmtMoney(clientCallTotal, currencySymbol)}) — the balance line is floored at zero and the effective call is larger than specified.`}
              />
            ) : null}
          </FormSection>

          <FormSection
            title='Call Items'
            titleColor='purple'
            scrollable={false}
            headerActions={
              readOnly ? null : (
                <>
                  {catchupOutstanding && !hasCatchupItems ? (
                    <Button size='small' onClick={addCatchupLines}>
                      Add catch-up lines
                    </Button>
                  ) : null}
                  <Button size='small' icon={<PlusOutlined />} onClick={addItem}>
                    Add Item
                  </Button>
                </>
              )
            }
          >
            {items.length === 0 ? (
              <Text type='secondary'>No call items — add one, or call management fees only below.</Text>
            ) : (
              <Table size='small' columns={itemColumns} dataSource={items} pagination={false} rowKey='key' />
            )}
            <Text type='secondary' className='block mt-2'>
              Each item is either a fixed amount or a percentage of closed commitments (the Type
              toggle). Overrides win over calculated amounts.
            </Text>
            {combinedItemInUse ? (
              <Alert
                className='mt-2'
                type='warning'
                showIcon
                message={`This fund's expenses are outside the commitment, but "${itemLabel(commitmentTerms?.combined_item_type_id)}" cannot be split: all of it counts toward the commitment. Call the expenses on "${itemLabel(commitmentTerms?.expenses_item_type_id)}" instead.`}
              />
            ) : null}
          </FormSection>

          <FormSection
            title='Management Fees'
            titleColor='orange'
            scrollable={false}
            headerActions={
              readOnly ? null : (
                <Button size='small' icon={<PlusOutlined />} onClick={addFeeItem}>
                  Add Fee Period
                </Button>
              )
            }
          >
            {feeItems.length === 0 ? (
              <Text type='secondary'>
                No management fees in this call — add a fee period and Compute to have the fee
                engine calculate it.
              </Text>
            ) : (
              <Table size='small' columns={feeColumns} dataSource={feeItems} pagination={false} rowKey='key' />
            )}
          </FormSection>

          <FormSection title='Totals' titleColor='green' scrollable={false}>
            {totalsRows.length === 0 ? (
              <Text type='secondary'>Add items to see totals.</Text>
            ) : (
              <Table
                size='small'
                pagination={false}
                rowKey='key'
                columns={[
                  { title: '', dataIndex: 'label' },
                  {
                    title: 'Fund Input',
                    dataIndex: 'fundInput',
                    width: 160,
                    align: 'right' as const,
                    render: (v: number | null) => currencyText(v)
                  },
                  {
                    title: 'Investor Aggregate',
                    dataIndex: 'investorAggregate',
                    width: 160,
                    align: 'right' as const,
                    render: (v: number | null) => currencyText(v)
                  }
                ]}
                dataSource={totalsRows}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row className='font-medium'>
                      <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align='right'>
                        {fmtMoney(totalsSum.fundInput, currencySymbol)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align='right'>
                        {fmtMoney(totalsSum.investorAggregate, currencySymbol)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            )}
            <Text type='secondary' className='block mt-2'>
              Fund Input = the fund-level effective amount (override if set, else calculated).
              Investor Aggregate = the sum of the per-commitment amounts below, which is what an
              issue books.
            </Text>
            {onTopLabel && (pctSized || totalMode === 'amount') ? (
              // addendum part 4 and the call's "Include ..." choices: which
              // lines this call adds on top of its amount
              <Text type='secondary' className='block mt-1'>
                {onTopLabel.charAt(0).toUpperCase() + onTopLabel.slice(1)} are{' '}
                {/* a choice the call made is not a term of the fund */}
                {feesInTotal == null && expensesInTotal == null ? 'outside the commitment: ' : ''}added on top of the{' '}
                {callAmountText}
                {clientCallTotal != null ? ` (${fmtMoney(clientCallTotal, currencySymbol)})` : ''}
                {onTopSum > 0 ? `, ${fmtMoney(Math.round(onTopSum * 100) / 100, currencySymbol)} in this call` : ''}.
              </Text>
            ) : null}
            {(detail?.totals?.fee_break_total ?? 0) > 0 ? (
              (pctSized || totalMode === 'amount') && !feesInTotalEffective ? (
                // fees on top of the call amount: the balance nets no fee,
                // so a break only lowers that investor's own fee line
                <Text type='secondary' className='block mt-1'>
                  Side-letter fee breaks of {fmtMoney(detail?.totals?.fee_break_total, currencySymbol)} lower
                  only the fee lines of the investors who hold them: the fees are added on top of the call
                  amount, so every investor pays its full pro-rata share of the balance and its own fee on top.
                </Text>
              ) : detail?.totals?.fee_break_in_full ? (
                // spec A3.9: investors contribute in full - a break is a
                // waived fee, never a smaller call. A call
                // issued under the earlier rule still carries reduced
                // amounts until it is reverted and re-issued.
                isIssued &&
                detail?.call?.prog__call_total_amount != null &&
                (detail?.totals?.called_total ?? 0) + 0.01 < Number(detail.call.prog__call_total_amount) ? (
                  <Text type='warning' className='block mt-1'>
                    This fund now has investors contribute in full, but this call was issued while fee
                    breaks still reduced investors&apos; calls: it called{' '}
                    {fmtMoney(
                      Number(detail.call.prog__call_total_amount) - (detail?.totals?.called_total ?? 0),
                      currencySymbol
                    )}{' '}
                    less than the gross ask. Revert and re-issue it (a consolidated call from the
                    consolidated fund&apos;s Calls tab) to call every investor its full share.
                  </Text>
                ) : (
                  <Text type='secondary' className='block mt-1'>
                    Side-letter fee breaks of {fmtMoney(detail?.totals?.fee_break_total, currencySymbol)} do
                    not reduce any call: this fund has investors contribute in full, so each pays its
                    pro-rata share and a reduced-fee investor&apos;s break is invested for it. How the
                    waived fees are settled is the fund&apos;s Waived Fee Settlement.
                  </Text>
                )
              ) : grossExact ? (
                <Text type='secondary' className='block mt-1'>
                  Side-letter fee breaks of {fmtMoney(detail?.totals?.fee_break_total, currencySymbol)} favour
                  the investors who hold them (each pays its break less than its pro-rata share); the
                  balance line absorbs them, so the total called is exactly the gross of{' '}
                  {fmtMoney(detail?.call?.prog__call_total_amount, currencySymbol)}.
                </Text>
              ) : (
                <Text type='secondary' className='block mt-1'>
                  Side-letter fee breaks of {fmtMoney(detail?.totals?.fee_break_total, currencySymbol)} come
                  off the investors who hold them, not the pool: the total called is the gross ask of{' '}
                  {fmtMoney(detail?.call?.prog__call_total_amount, currencySymbol)} less those breaks.
                </Text>
              )
            ) : null}
          </FormSection>

          <FormSection title='Per-Commitment Allocation' titleColor='indigo' scrollable={false}>
            {isNewRecord() ? (
              <Text type='secondary'>Save the call, then Compute to allocate amounts per commitment.</Text>
            ) : perCommitmentRows.length === 0 ? (
              <Text type='secondary'>No allocations yet — run Compute to populate this table.</Text>
            ) : (
              <>
                {dirty ? (
                  <Alert
                    className='mb-2'
                    type='info'
                    showIcon
                    message='Unsaved changes: Save keeps your per-investor edits as overrides. Recompute saves them first, then asks whether to keep or discard manual edits.'
                  />
                ) : null}
                {consolidatedBasis === 'consolidated' ? (
                  // what the amounts are sized on, so a call reads as a % of
                  // the Consolidated Commitment and not of the primary pocket
                  <Text type='secondary' className='block mb-2'>
                    Consolidated call: amounts are sized on each investor&apos;s Consolidated Commitment
                    {(detail?.call?.prog__consolidated_fund_names ?? []).length > 0
                      ? ` (${(detail?.call?.prog__consolidated_fund_names ?? []).join(' + ')})`
                      : ''}
                    .
                  </Text>
                ) : null}
                <CallAllocationTable
                  rows={perCommitmentRows}
                  layout={allocLayout}
                  itemTypes={pivotItemTypes}
                  pcOverride={pcOverride}
                  setPcOverride={setPcOverride}
                  readOnly={readOnly}
                  currencyFmt={currencyFmt}
                  currencyText={currencyText}
                  lateCloserCommitmentIds={lateCloserCommitmentIds}
                  hasFeeBreaks={hasFeeBreaks}
                  feeBreakLabel={feeBreakLabel}
                  rowFeeBreak={rowFeeBreak}
                />
              </>
            )}
          </FormSection>

          {/* the notices stored when the call is issued (previewed while
              Draft); a transfer-derived call sends none */}
          {detail?.call?.id && !isTransferCall ? (
            <NoticesSection capitalCallId={detail.call.id} version={detail} />
          ) : null}
          </>
          )}

          {linkedTransactionCard}
        </div>
      </Card>
    </div>
  )
}
